import { useLayoutEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import '../../../styles/css/revamp_style.css';
import AbilityMarkersReport from './AbilityMarkersReport';
import { CampusProvider } from './campusContext';
import {
    EventsReport,
    GuidanceReport,
    PlacementsReport,
    RewardsReport,
    StoriesReport,
    UploadLogReport,
} from './ReportPages';
import { DEFAULT_REPORT_ID, ReportDef, reportById } from './reportRegistry';
import { SampleFileProvider, useSampleFileAction } from './sampleFileContext';

const renderReport = (report: ReportDef) => {
    switch (report.id) {
        case 'events':
            return <EventsReport report={report} />;
        case 'placements':
            return <PlacementsReport report={report} />;
        case 'rewards':
            return <RewardsReport report={report} />;
        case 'guidance':
            return <GuidanceReport report={report} />;
        case 'stories':
            return <StoriesReport report={report} />;
        case 'markers':
            return <AbilityMarkersReport report={report} />;
        case 'uploadlog':
            return <UploadLogReport report={report} />;
        default:
            return <EventsReport report={report} />;
    }
};

const CustomReportsShell = () => {
    const { reportId } = useParams();
    return (
        <CampusProvider key={reportId || 'none'}>
            <SampleFileProvider>
                <CustomReportsPage />
            </SampleFileProvider>
        </CampusProvider>
    );
};

const CustomReportsPage = () => {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const report = reportById(reportId);
    const sampleFile = useSampleFileAction();

    useLayoutEffect(() => {
        if (!reportId || !report) {
            navigate(`/support/custom-reports/${DEFAULT_REPORT_ID}`, { replace: true });
            return;
        }
        document.title = report.label;
    }, [reportId, report, navigate]);

    if (!reportId || !report) {
        return <Navigate to={`/support/custom-reports/${DEFAULT_REPORT_ID}`} replace />;
    }

    return (
        <div className="main flex-1">
            <div className="cr-page">
                <div className="cr-page-head">
                    <div>
                        <h1>{report.label}</h1>
                        <p>{report.description}</p>
                    </div>
                    <div className="cr-page-actions">
                        {sampleFile && (
                            <button
                                type="button"
                                className="cr-btn cr-btn-outline"
                                disabled={!sampleFile.uploadReady}
                                title={
                                    sampleFile.uploadReady
                                        ? `Download ${sampleFile.report}-sample.xlsx`
                                        : 'Select a campus before downloading the sample file'
                                }
                                onClick={sampleFile.download}>
                                <i className="bi bi-file-earmark-arrow-down me-1"></i>Sample file
                            </button>
                        )}
                        <button
                            type="button"
                            className="cr-btn cr-btn-navy"
                            disabled={!sampleFile?.upload || !sampleFile.uploadReady}
                            title={
                                !sampleFile?.upload
                                    ? 'Upload is available on Event Participation and Rewards & Recognitions'
                                    : sampleFile.uploadReady
                                      ? 'Choose an Excel file'
                                      : 'Select a campus before uploading'
                            }
                            onClick={() => sampleFile?.upload?.()}>
                            <i className="bi bi-cloud-upload me-1"></i>Upload Data
                        </button>
                    </div>
                </div>
                <div className="cr-main">{renderReport(report)}</div>
            </div>
        </div>
    );
};

export default CustomReportsShell;
